import React, { ReactNode, useState, useEffect } from 'react';
import produce from "immer";

import './Config.scss';
import { DeleteModal, ErrorMessage, Page, PageHeader } from 'components';
import { LoadState } from 'enums';
import { ICaptureConfig, IReplayConfig, DefaultCaptureConfig, DefaultReplayConfig } from 'interfaces';
import { CaptureConfigModal } from './modals/CaptureConfigModal';
import { ReplayConfigModal } from './modals/ReplayConfigModal';
import { CaptureConfigItem } from './items/CaptureConfigItem'
import { ReplayConfigItem } from './items/ReplayConfigItem'
import { Button, Tabs, TabPanel, TabItem, LoadingIndicator, Modal, Row, TextField, Col } from 'cfa-react-components';
import { CaptureConfigService, ReplayConfigService } from 'services';
import { Toaster } from 'react-hot-toast';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

interface IConfigPageState {
    // load state
    loadStatus: LoadState;
    loadError?: string;
    // selections for edits
    selectedCaptureConfig: ICaptureConfig;
    selectedReplayConfig: IReplayConfig;
    // modals
    showCaptureConfigModal?: boolean;
    showReplayConfigModal?: boolean;
    editing?: boolean,
    // delete confirmation
    showDeleteModal?: boolean;
    deleteTarget?: any,
    deleteFunc?: (any) => Promise<any>,
    deleteVerb?: string;
    deleteNoun?: string;
}

export function Config() {

    const [pageState, setPageState] = useState<IConfigPageState>({
        loadStatus: LoadState.Loading,
        selectedCaptureConfig: DefaultCaptureConfig,
        selectedReplayConfig: DefaultReplayConfig,
    });
    const { loadStatus, loadError, selectedCaptureConfig, selectedReplayConfig, showCaptureConfigModal, showReplayConfigModal, editing, showDeleteModal, deleteTarget, deleteFunc, deleteVerb, deleteNoun } = pageState;

    const [captureConfigs, setCaptureConfigs] = useState<ICaptureConfig[]>([]);
    const [replayConfigs, setReplayConfigs] = useState<IReplayConfig[]>([]);

    const [selectedTabItemKey, setSelectedTabItemKey] = useState("capture");

    useEffect(() => {
        if (loadStatus === LoadState.Success) return;

        Promise.all([
            CaptureConfigService.getAll(),
            ReplayConfigService.getAll(),
        ]).then(() => {
            setCaptureConfigs(CaptureConfigService.allCaptureConfigs);
            setReplayConfigs(ReplayConfigService.allReplayConfigs);
            setPageState(produce(draft => {
                draft.loadStatus = LoadState.Success;
            }));
        });
    });

    // Capture config modal
    const openCaptureConfigModal = () => {
        setPageState(produce(draft => {
            draft.showCaptureConfigModal = true;
            draft.editing = false;
        }))
    }

    const closeCaptureConfigModal = () => {
        setPageState(produce(draft => {
            draft.showCaptureConfigModal = false;
            draft.selectedCaptureConfig = DefaultCaptureConfig;
            draft.editing = false;
        }))
    }

    const onEditCaptureConfig = (c: ICaptureConfig) => {
        setPageState(produce(draft => {
            draft.selectedCaptureConfig = c;
            draft.showCaptureConfigModal = true;
            draft.editing = true;
        }))
    }

    // Replay modal
    const openReplayConfigModal = () => {
        setPageState(produce(draft => {
            draft.showReplayConfigModal = true;
            draft.editing = false;
        }))
    }

    const closeReplayConfigModal = () => {
        setPageState(produce(draft => {
            draft.showReplayConfigModal = false;
            draft.selectedReplayConfig = DefaultReplayConfig;
            draft.editing = false;
        }))
    }

    const onEditReplayConfig = (r: IReplayConfig) => {
        setPageState(produce(draft => {
            draft.selectedReplayConfig = r;
            draft.showReplayConfigModal = true;
            draft.editing = true;
        }))
    }

    // delete modal
    const openDeleteModal = (target: any, deleteFunc: (any) => Promise<any>, deleteVerb: string, deleteNoun: string) => {
        setPageState(produce(draft => {
            draft.showDeleteModal = true;
            draft.deleteTarget = target;
            draft.deleteFunc = deleteFunc;
            draft.deleteVerb = deleteVerb;
            draft.deleteNoun = deleteNoun;
        }))
    }

    const closeDeleteModal = () => {
        setPageState(produce(draft => {
            draft.showDeleteModal = false;
            draft.deleteTarget = undefined;
            draft.deleteFunc = undefined;
            draft.deleteVerb = undefined;
            draft.deleteNoun = undefined;
        }))
    }

    // refresh application data
    const refreshData = () => {
        setCaptureConfigs([]);
        setReplayConfigs([]);
        // set to loading here to refresh using the useEffect above
        setPageState(produce(draft => {
            draft.loadStatus = LoadState.Loading;
        }));
    }

    // refresh list
    const refresh = () => {
        setCaptureConfigs(CaptureConfigService.allCaptureConfigs);
        setReplayConfigs(ReplayConfigService.allReplayConfigs);
    }

    // filter capture config list
    const filterCaptureConfigs = (e) => {
        let text = e.target.value.toLowerCase();
        let caps = CaptureConfigService.allCaptureConfigs.filter(c => {
            // search capture name and source type
            if (c.name.toLowerCase().indexOf(text) != -1) return c;
            if (c.sourceType.toLowerCase().indexOf(text) != -1) return c;
        });

        setCaptureConfigs(caps);
    }

    // filter replay config list
    const filterReplayConfigs = (e) => {
        let text = e.target.value.toLowerCase();
        let reps = ReplayConfigService.allReplayConfigs.filter(r => {
            // search replay name and sink type
            if (r.name.toLowerCase().indexOf(text) != -1) return r;
            if (r.sinkType.toLowerCase().indexOf(text) != -1) return r;
        });

        setReplayConfigs(reps);
    }

    // Content
    const pageHeader: ReactNode = (
        <Row style={{float: "right"}}>
            <Button className="refreshButton" loading={loadStatus == LoadState.Loading} variant="outlined" onClick={refreshData}>
                <FontAwesomeIcon icon="redo" className="refreshIcon" />
            </Button>
            {selectedTabItemKey === "capture" && (
                <Button onClick={openCaptureConfigModal}>{'New Capture Config'}</Button>
            )}
            {selectedTabItemKey === "replay" && (
                <Button onClick={openReplayConfigModal}>{'New Replay Config'}</Button>
            )}
        </Row>
    );

    const captureConfigTableHeaders = (captureConfigs.length > 0 && (
        <tr className="configTable_headers">
            <th className="configTable_header">Capture Config Name</th>
            <th className="configTable_header">Source Type</th>
            <th className="configTable_header">Source Location</th>
            <th className="configTable_header">Source Secret Name</th>
            <th className="configTable_header">Duration (Mins)</th>
            <th className="configTable_header">Delay (Mins)</th>
            <th className="configTable_header">TTL (Days)</th>
            <th className="configTable_header"></th>
        </tr>
    ));

    let captureConfigTableContent: ReactNode;
    switch (loadStatus) {
        case LoadState.Initial:
        case LoadState.Loading:
        case LoadState.Success:
            if (captureConfigs.length > 0 || loadStatus === LoadState.Loading) {
                captureConfigTableContent = (<>
                    {captureConfigs.map(captureConfig => (
                        <CaptureConfigItem 
                            key={captureConfig.captureConfigUUID}
                            config={captureConfig}
                            onEditCaptureConfig={onEditCaptureConfig}
                            openDeleteModal={openDeleteModal}
                            refresh={refresh}
                        />
                    ))}
                    {loadStatus === LoadState.Loading && (
                        <LoadingIndicator size="md" variant="inline" style={{margin: "24px"}}/>
                    )}
                </>);
            } else {
                captureConfigTableContent = (
                    <tr><td className='configTable_item'>No capture configs found</td></tr>
                );
            }
            break;
        case LoadState.Error:
            captureConfigTableContent = (
                <ErrorMessage message={loadError || 'Error loading.'} />
            );
            break;
    }

    const replayConfigTableHeaders = (replayConfigs.length > 0 && (
        <tr className="configTable_headers">
            <th className="configTable_header">Replay Config Name</th>
            <th className="configTable_header">Sink Type</th>
            <th className="configTable_header">Sink Location</th>
            <th className="configTable_header">Sink Secret Name</th>
            <th className="configTable_header">Timing Option</th>
            <th className="configTable_header"></th>
        </tr>
    ));

    let replayConfigTableContent: ReactNode;
    switch (loadStatus) {
        case LoadState.Initial:
        case LoadState.Loading:
        case LoadState.Success:
            if (replayConfigs.length > 0 || loadStatus === LoadState.Loading) {
                replayConfigTableContent = (<>
                    {replayConfigs.map(replayConfig => (
                        <ReplayConfigItem 
                            key={replayConfig.replayConfigUUID}
                            config={replayConfig}
                            onEditReplayConfig={onEditReplayConfig}
                            openDeleteModal={openDeleteModal}
                            refresh={refresh}
                        />
                    ))}
                    {loadStatus === LoadState.Loading && (
                        <LoadingIndicator size="md" variant="inline" style={{margin: "24px"}}/>
                    )}
                </>);
            } else {
                replayConfigTableContent = (
                    <tr><td className='configTable_item'>No replay configs found</td></tr>
                );
            }
            break;
        case LoadState.Error:
            replayConfigTableContent = (
                <ErrorMessage message={loadError || 'Error loading.'} />
            );
            break;
    }

    // page tabs
    const pageTabs: ReactNode = (
        <Tabs activeItemKey={selectedTabItemKey} onChange={(i) => {
            setSelectedTabItemKey(i);
            refresh(); // reset filter
        }}>
            <TabItem itemKey='capture'>
                Capture Configs
            </TabItem>
            <TabItem itemKey='replay'>
                Replay Configs
            </TabItem>
        </Tabs>
    );

    return (<>
        <Page>
            <PageHeader title={'Config'} right={pageHeader}/>
            {pageTabs}
            <Row>
                <Col>
                    {selectedTabItemKey == "capture" && <TextField 
                        label={'Filter'} 
                        placeholder='Enter capture config name or source type...'
                        onChange={filterCaptureConfigs}
                    />}
                    {selectedTabItemKey == "replay" && <TextField
                        label={'Filter'} 
                        placeholder='Enter replay config name or sink type...'
                        onChange={filterReplayConfigs}
                    />}
                </Col>
            </Row>
            <TabPanel itemKey='capture' activeItemKey={selectedTabItemKey}>
                <table className="configTable">
                    <thead>{captureConfigTableHeaders}</thead>
                    <tbody>{captureConfigTableContent}</tbody>
                </table>
            </TabPanel>
            <TabPanel itemKey='replay' activeItemKey={selectedTabItemKey}>
                <table className="configTable">
                    <thead>{replayConfigTableHeaders}</thead>
                    <tbody>{replayConfigTableContent}</tbody>
                </table>
            </TabPanel>
        </Page>

        <Toaster />
        
        {/* wrap components in modal so that the contents are unmounted on close */}
        <Modal show={showCaptureConfigModal} onClose={closeCaptureConfigModal} size='lg'>
            <CaptureConfigModal
                key={0}
                config={selectedCaptureConfig}
                editing={editing}
                onHide={closeCaptureConfigModal}
            />
        </Modal>
        
        <Modal show={showReplayConfigModal} onClose={closeReplayConfigModal} size='lg'>
            <ReplayConfigModal
                key={1}
                config={selectedReplayConfig}
                editing={editing}
                onHide={closeReplayConfigModal}
            />
        </Modal>

        <Modal show={showDeleteModal} onClose={closeDeleteModal} size='sm'>
            <DeleteModal 
                key={2} 
                target={deleteTarget} 
                targetFunc={deleteFunc} 
                onHide={closeDeleteModal} 
                refresh={refresh} 
                deleteVerb={deleteVerb} 
                deleteNoun={deleteNoun}
            />
        </Modal>
    </>);
}
